import React, { useRef, useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import Lottie from "lottie-react";
import loading from "../lottie/loading.json";
import api, { handleAxiosError } from "../config/api";
import {
  trackMachineCodeScreenView,
  trackMachineCodeCameraOpen,
  trackMachineCodeScanSuccess,
  trackMachineCodeManualEntry,
  trackMachineCodeValidated,
  trackMachineCodeRejected,
  trackHowToPlayView,
} from '../utils/ga4Events';
import MenuPopup from '../components/MenuPopup';
import { getPostPackScanTarget } from '../utils/journeyFlow';
import { redirectToRoute } from '../utils/routeRedirect';
import BuyMore from '../components/BuyMore';

const MACHINE_WELCOME_SEEN_KEY = 'britannia_machine_welcome_seen'

function dataURLtoFile(dataUrl, filename) {
  const [header, data] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)?.[1] || "image/jpeg";
  const binary = atob(data);
  const arr = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i);
  return new File([arr], filename, { type: mime });
}

function MachineCodeScan() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const streamRef = useRef(null)
  const [showMenu, setShowMenu] = useState(false);
  const [cameraActive, setCameraActive] = useState(false)
  const [popupType, setPopupType] = useState('scan') // scan | loading | success | error | duplicate
  const [capturedImage, setCapturedImage] = useState(null)
  const [showPopup, setShowPopup] = useState(false)
  const [popupMessage, setPopupMessage] = useState("")
  const [popupBuy, setPopupBuy] = useState(false);
  const [cameraAccessError, setCameraAccessError] = useState(false)

  const getScanErrorMessage = useCallback((serverMessage, fallback) => {
    const msg = String(serverMessage || "");
    if (/mobilenumber\s+must\s+be\s+a\s+valid\s+mobile\s+number/i.test(msg)) {
      return t("scan.invalidMobile");
    }
    return serverMessage || fallback;
  }, [t]);

  // ── Welcome popup: show only on first visit to this screen ──

  const language = localStorage.getItem("appLanguage") || "en";
  const [showWelcome, setShowWelcome] = useState(() => {
    return localStorage.getItem(MACHINE_WELCOME_SEEN_KEY) !== "true";
  })

  const scanStartTimeRef = useRef(null);
  const machineCodeRejectedCountRef = useRef(0);

  // Fire screen view event and how_to_play_view for first-visit welcome popup
  useEffect(() => {
    const entryPoint = document.referrer.includes('/home')
      ? 'from_home'
      : document.referrer.includes('/otp')
        ? 'from_registration'
        : 'from_home';
    trackMachineCodeScreenView({ entry_point: entryPoint });
  }, []);

  useEffect(() => {
    if (showWelcome) {
      trackHowToPlayView({ auto_shown_vs_tapped: 'auto' });
    }
  }, [showWelcome]);

  function handleWelcomeClose() {
    localStorage.setItem(MACHINE_WELCOME_SEEN_KEY, 'true')
    setShowWelcome(false)
  }

  // ── Camera ────────────────────────────────────────────────
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { exact: 'environment' } },
        audio: false,
      })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        videoRef.current.play()
      }
      setCameraActive(true)
      trackMachineCodeCameraOpen({
        device_model: navigator.userAgent,
        os_version: navigator.platform || '',
      })
    } catch (err) {
      // Permission denied — show error popup immediately, don't try fallback
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setCameraAccessError(true)
        return
      }

      // Rear camera not found (OverconstrainedError / NotFoundError) — try any camera
      console.warn('Rear camera not available, trying any camera...', err)
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        })
        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          videoRef.current.play()
        }
        setCameraActive(true)
        trackMachineCodeCameraOpen({
          device_model: navigator.userAgent,
          os_version: navigator.platform || '',
        })
      } catch (fallbackErr) {
        // Fallback also failed (permission denied mid-session, no camera hardware, etc.)
        setCameraAccessError(true)
      }
    }
  }, [])

  useEffect(() => {
    startCamera()
  }, [])

  const handleGrantAccess = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      stream.getTracks().forEach(t => t.stop());
      window.location.reload();
    } catch (err) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        window.location.reload();
      }
    }
  }, []);

  // ── Capture frame from video ──────────────────────────────
  const captureFrame = useCallback(() => {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas) return null
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    canvas.getContext('2d').drawImage(video, 0, 0)
    return canvas.toDataURL('image/jpeg', 0.8)
  }, [])

  // ── Capture button: start camera OR capture + show popup ──
  const handleCaptureBtn = useCallback(async () => {
    if (!cameraActive) {
      await startCamera()
    } else {
      const img = captureFrame()
      if (!img) return;

      setCapturedImage(img)
      setPopupMessage("")
      setPopupType('loading')
      setShowPopup(true)
      scanStartTimeRef.current = Date.now();

      try {
        const mobile =
          localStorage.getItem("userMobile") ||
          localStorage.getItem("mobile") ||
          "";

        const formData = new FormData();
        formData.append("image", dataURLtoFile(img, "sku.jpg"));
        formData.append("mobilenumber", mobile);

        const response = await api.post("/sku/upload-sku", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        });

        // Success
        if (response?.data?.status === "VALID") {
          const scanDuration = scanStartTimeRef.current ? Date.now() - scanStartTimeRef.current : null;
          trackMachineCodeScanSuccess({ scan_duration_ms: scanDuration });
          trackMachineCodeValidated({
            validation_result: 'valid',
            sku_detected: response?.data?.skuCode || response?.data?.sku_code || '',
          });
          localStorage.setItem("machineCodeScanned", "true");
          setPopupType("success");
          return;
        }

        // Fallback for unexpected success shape
        const scanDurationFb = scanStartTimeRef.current ? Date.now() - scanStartTimeRef.current : null;
        trackMachineCodeScanSuccess({ scan_duration_ms: scanDurationFb });
        trackMachineCodeValidated({ validation_result: 'valid', sku_detected: '' });
        localStorage.setItem("machineCodeScanned", "true");
        setPopupType("success");
      } catch (err) {
        const serverMessage = String(
          err?.response?.data?.message ||
          err?.response?.data?.reason ||
          err?.response?.data?.error ||
          ""
        );

        let msgKey = "scanFailed";

        if (/already\s*scanned/i.test(serverMessage)) {
          msgKey = "machineCodeAlreadyScanned";
        } else if (/mobile.*mismatch/i.test(serverMessage)) {
          msgKey = "scanFailed";
        } else if (/not configured/i.test(serverMessage)) {
          msgKey = "notConfigured";
        } else if (/invalid.*sku|invalid.*image|invalid.*response/i.test(serverMessage)) {
          msgKey = "invalidSku";
        } else if (/service|unavailable|timeout|ECONNREFUSED/i.test(serverMessage)) {
          msgKey = "serviceError";
        } else if (/mobilenumber\s+must\s+be\s+a\s+valid\s+mobile\s+number/i.test(serverMessage)) {
          msgKey = "scanFailed";
        }

        setPopupMessage(t(`scan.${msgKey}`));
        machineCodeRejectedCountRef.current += 1;
        const rejectionReason = /already\s*scanned/i.test(serverMessage)
          ? 'already_used'
          : /invalid/i.test(serverMessage)
            ? 'invalid'
            : 'format_error';
        trackMachineCodeRejected({
          rejection_reason: rejectionReason,
          attempt_count: machineCodeRejectedCountRef.current,
        });
        setPopupType(
          /already\s*scanned/i.test(serverMessage) ? "duplicate" : "error"
        );
      }
    }
  }, [cameraActive, startCamera, captureFrame, getScanErrorMessage, t])

  // ── Close / dismiss popup ─────────────────────────────────
  const handleClosePopup = useCallback(() => {
    setShowPopup(false)
    setCapturedImage(null)
    setPopupMessage("")
    setPopupType('scan')
  }, [])

  // ── Continue after success ────────────────────────────────
  const handleContinue = useCallback(() => {
    localStorage.setItem("machineCodeScanned", "true");
    redirectToRoute(navigate, getPostPackScanTarget());
  }, [navigate]);

  return (
    <>
      <meta charSet="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
      <link rel="shortcut icon" href="assets/images/favicon.ico" type="image/x-icon" />
      <link rel="stylesheet" href="assets/css/styles.css?ver=1.7" />
      <title>Batch Scan | Britannia</title>

      {/* ══════════════════════════════════════════════════════
          WELCOME POPUP — shows only on first visit to this page
          Replace  assets/images/machine-welcome.webp  with your
          actual static banner / instruction image.
      ══════════════════════════════════════════════════════ */}
      {showWelcome && (
        <div
          className="scan-popup"
          onClick={(e) => e.target === e.currentTarget && handleWelcomeClose()}
        >
          {/* Rays background */}
          <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />

          <div className="popup-card">
            {/* Sparkle */}
            <img
              src="assets/images/star-element.webp"
              className="popup-star"
              alt="Sparkle Element"
            />

            <div className="popup-inner">
              {/* Heading text */}
              <p className="popup-text">
                {popupType === 'scan' ? (
                  <>
                    {t("scan.machinePrompt").split("\n")[0]}
                    <br />
                    {t("scan.machinePrompt").split("\n")[1]}
                  </>
                ) : (
                  <>
                    {t("scan.machineSuccess").split("\n")[0]}
                    <br />
                    <strong>{t("scan.machineSuccess").split("\n")[1]}</strong>
                  </>
                )}
              </p>

              {/* Image area */}
              <div className="popup-image">
                {popupType === 'scan' ? (
                  /*
                    Shows the live captured frame from the camera.
                    Tapping it marks the machine code as found → success state.
                    Falls back to the static sample if no frame was captured.
                  */
                  <video
                    src="/assets/images/how-to-scan.mp4"
                    autoPlay
                    loop
                    muted
                    playsInline // Important for iOS support
                    style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                  />
                ) : (
                  <img
                    src="/assets/images/success.webp"
                    alt="Success"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                )}
              </div>
            </div>

            {/* Action button */}
            <button
              className="btn-next popup-btn"
              type="button"
              onClick={handleWelcomeClose}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
              }}
            >
              <span>{t("common.continue")}</span>

              <svg
                width="20"
                height="20"
                viewBox="0 0 55 43"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  display: "block",
                  flexShrink: 0,
                }}
              >
                <path
                  d="M27.3634 31.2726C23.0456 31.2726 19.5453 27.7723 19.5453 23.4544C19.5453 19.1366 23.0456 15.6362 27.3634 15.6362C31.6813 15.6362 35.1816 19.1366 35.1816 23.4544C35.1816 27.7723 31.6813 31.2726 27.3634 31.2726Z"
                  fill="black"
                />
                <path
                  d="M5.86385 7.81819H13.0712C13.4377 7.81819 13.8922 7.5812 14.2464 7.20739L17.4152 2.2062C17.4653 2.12682 17.5213 2.05129 17.5826 1.9802C18.6771 0.703637 20.1565 0 21.7445 0H32.9832C34.5713 0 36.0506 0.703637 37.1452 1.9802C37.2065 2.05129 37.2624 2.12682 37.3125 2.2062L40.4813 7.21472C40.7525 7.51035 41.1337 7.82552 41.5343 7.82552V6.84825C41.5343 6.32987 41.7403 5.83272 42.1068 5.46617C42.4734 5.09962 42.9705 4.8937 43.4889 4.8937H46.4207C46.9391 4.8937 47.4362 5.09962 47.8028 5.46617C48.1693 5.83272 48.3753 6.32987 48.3753 6.84825V7.82552H48.8639C50.4185 7.82714 51.909 8.44543 53.0083 9.54473C54.1076 10.644 54.7259 12.1345 54.7275 13.6892V37.1364C54.7259 38.691 54.1076 40.1815 53.0083 41.2808C51.909 42.3801 50.4185 42.9984 48.8639 43H5.86385C4.30921 42.9984 2.81871 42.3801 1.71941 41.2808C0.620113 40.1815 0.00182343 38.691 0.000205994 37.1364V13.6818C0.00182343 12.1272 0.620113 10.6367 1.71941 9.5374C2.81871 8.4381 4.30921 7.81981 5.86385 7.81819ZM27.3639 35.1819C29.6833 35.1819 31.9507 34.4941 33.8792 33.2055C35.8077 31.9168 37.3109 30.0853 38.1985 27.9424C39.0861 25.7995 39.3183 23.4416 38.8658 21.1667C38.4133 18.8918 37.2964 16.8022 35.6563 15.1621C34.0162 13.522 31.9266 12.4051 29.6518 11.9526C27.3769 11.5001 25.0189 11.7324 22.876 12.62C20.7332 13.5076 18.9016 15.0107 17.613 16.9392C16.3244 18.8678 15.6366 21.1351 15.6366 23.4546C15.6401 26.5637 16.8768 29.5446 19.0754 31.7431C21.2739 33.9416 24.2547 35.1783 27.3639 35.1819Z"
                  fill="black"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="page-wrapper">
        <div className="main-wrapper yellow-bg">

          {/* ══ DESKTOP SCREEN ══ */}
          <div className="d-frame-outer desktop-only">
            <img src="assets/images/desktop-star.webp" className="d-frame-star-bg" alt="" />
            <span className="d-frame-corner tl" />
            <span className="d-frame-corner tr" />
            <span className="d-frame-corner bl" />
            <span className="d-frame-corner br" />
            <div className="d-frame-border-outer">
              <div className="d-frame-border">
                <div className="d-frame-border-inner">
                  <div className="d-frame-canvas">
                    <div className="d-frame-content">
                      <div className="desktop-center">
                        <div className="desktop-content-box">
                          <h3>{t("common.desktopInterest")}</h3>
                          <img
                            src="assets/images/desktop-logo-milk-bikis.webp"
                            className="desktop-logo"
                            alt="Milk Bikis Logo"
                          />
                          <div className="desktop-qr-box">
                            <span className="qr-corner qr-corner-tl" />
                            <span className="qr-corner qr-corner-tr" />
                            <span className="qr-corner qr-corner-bl" />
                            <span className="qr-corner qr-corner-br" />
                            <div className="qr-left">
                              <p className="qr-text">
                                {t("common.desktopQr")}
                              </p>
                            </div>
                            <div className="qr-right">
                              <img src="assets/images/desktop-qr.svg" alt="QR Code" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ══ MOBILE CAMERA SCREEN ══ */}
          <div className="frame-yellow-bg mobile-only">
            <div className="frame-green-corners">
              <div className="frame-canvas br-16">
                <div className="frame-content">


                  <div className="container-full">
                    <div className="top-logo">
                      <div className="camera-sc-logo-wrap">
                        <img src="assets/images/animate-britania-logo.webp" alt="Britannia" className="camera-sc-logo camera-sc-logo-1" />

                        <img src="assets/images/animate-milk-bikkis-logo.webp" alt="Milk Bikis" className="camera-sc-logo camera-sc-logo-2" />
                      </div>
                      <img src="assets/images/sign-up-head-text.webp" className="top-logo-text" alt="Top Logo" onClick={() => navigate("/home")} />

                      <div className="menu-bars" onClick={() => navigate("/menu")}>
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                    <div className="camera-container">
                      <p className="scan-text">{t("scan.packDetails")}</p>

                      <div className="scanner-box">
                        <video
                          ref={videoRef}
                          autoPlay
                          playsInline
                          muted
                          style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                        <canvas ref={canvasRef} style={{ display: 'none' }} />
                        <img src="assets/images/corner-tl.webp" className="corner tl" alt="" />
                        <img src="assets/images/corner-tr.webp" className="corner tr" alt="" />
                        <img src="assets/images/corner-bl.webp" className="corner bl" alt="" />
                        <img src="assets/images/corner-br.webp" className="corner br" alt="" />
                      </div>

                      <button
                        className="camera-btn"
                        type="button"
                        onClick={handleCaptureBtn}
                        disabled={showPopup && popupType === "loading"}
                      >
                        <img src="assets/images/camera-icon.webp" alt="Capture" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
          SCAN POPUP
          • popupType === 'scan'    → shows captured camera image
                                      tap image → switches to success
          • popupType === 'success' → shows success image
      ══════════════════════════════════════════════════════ */}
      {showPopup && (
        <div
          className="scan-popup"
          onClick={(e) => e.target === e.currentTarget && handleClosePopup()}
        >
          {/* Rays background */}
          <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />

          {popupType === "loading" && (
            <div style={{ maxWidth: 320, margin: "0 auto" }}>
              <Lottie animationData={loading} loop={true} />
            </div>
          )}

          {popupType === "success" && (
            <div className="scan-success-level-1-box">
              <div className="scan-success-level-1-box-scroll">
                <div className="scan-success-level-1-box-inner">
                  <div className="star-rotate">
                    <img src="assets/images/Star-2.webp" className="orange-star-bg img-fit" />
                  </div>
                  <div className="scan-success-level-1-box-text">
                    <div className="cashback-wrapper">
                      <img
                        src={language === "ta" ? "assets/images/machineCodeScan-tamil.webp" : "assets/images/your-scan-was-SUCCESSFul.webp"}
                        className="cashback-icon"
                      />
                    </div>
                  </div>
                </div>
                <div className="scan-success-level-1-box-footer">
                  <button className="btn-next claim-reward-btn-level-1" type="button" onClick={handleContinue}>
                    <span>{t("common.continue")}</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {(popupType === "error" || popupType === "duplicate") && (
            <>
              <div className="popup-card">
                <img
                  src="assets/images/star-element.webp"
                  className="popup-star"
                  alt="Sparkle Element"
                />
                <div className="popup-inner">
                  <p className="popup-text">
                    <span>
                      {popupMessage
                        ? popupMessage.split("\n")[0]
                        : t("scan.scanFailed").split("\n")[0]}
                    </span>
                    {(popupMessage?.split("\n")[1] || t("scan.scanFailed").split("\n")[1]) && (
                      <>
                        <br />
                        <span style={{ fontWeight: "bold" }}>
                          {popupMessage?.split("\n")[1] || t("scan.scanFailed").split("\n")[1]}
                        </span>
                      </>
                    )}
                  </p>
                  <div className="popup-image">
                    <video
                      src="/assets/images/how-to-scan.mp4"
                      autoPlay
                      loop
                      muted
                      playsInline // Important for iOS support
                      style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    alignItems: "center",
                  }}
                >
                  {/* Continue Button */}
                  <button
                    className="btn-next popup-btn"
                    type="button"
                    onClick={handleClosePopup}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                    }}
                  >
                    <span>{t("common.continue")}</span>

                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 55 43"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{
                        display: "block",
                        flexShrink: 0,
                      }}
                    >
                      <path
                        d="M27.3634 31.2726C23.0456 31.2726 19.5453 27.7723 19.5453 23.4544C19.5453 19.1366 23.0456 15.6362 27.3634 15.6362C31.6813 15.6362 35.1816 19.1366 35.1816 23.4544C35.1816 27.7723 31.6813 31.2726 27.3634 31.2726Z"
                        fill="black"
                      />
                      <path
                        d="M5.86385 7.81819H13.0712C13.4377 7.81819 13.8922 7.5812 14.2464 7.20739L17.4152 2.2062C17.4653 2.12682 17.5213 2.05129 17.5826 1.9802C18.6771 0.703637 20.1565 0 21.7445 0H32.9832C34.5713 0 36.0506 0.703637 37.1452 1.9802C37.2065 2.05129 37.2624 2.12682 37.3125 2.2062L40.4813 7.21472C40.7525 7.51035 41.1337 7.82552 41.5343 7.82552V6.84825C41.5343 6.32987 41.7403 5.83272 42.1068 5.46617C42.4734 5.09962 42.9705 4.8937 43.4889 4.8937H46.4207C46.9391 4.8937 47.4362 5.09962 47.8028 5.46617C48.1693 5.83272 48.3753 6.32987 48.3753 6.84825V7.82552H48.8639C50.4185 7.82714 51.909 8.44543 53.0083 9.54473C54.1076 10.644 54.7259 12.1345 54.7275 13.6892V37.1364C54.7259 38.691 54.1076 40.1815 53.0083 41.2808C51.909 42.3801 50.4185 42.9984 48.8639 43H5.86385C4.30921 42.9984 2.81871 42.3801 1.71941 41.2808C0.620113 40.1815 0.00182343 38.691 0.000205994 37.1364V13.6818C0.00182343 12.1272 0.620113 10.6367 1.71941 9.5374C2.81871 8.4381 4.30921 7.81981 5.86385 7.81819ZM27.3639 35.1819C29.6833 35.1819 31.9507 34.4941 33.8792 33.2055C35.8077 31.9168 37.3109 30.0853 38.1985 27.9424C39.0861 25.7995 39.3183 23.4416 38.8658 21.1667C38.4133 18.8918 37.2964 16.8022 35.6563 15.1621C34.0162 13.522 31.9266 12.4051 29.6518 11.9526C27.3769 11.5001 25.0189 11.7324 22.876 12.62C20.7332 13.5076 18.9016 15.0107 17.613 16.9392C16.3244 18.8678 15.6366 21.1351 15.6366 23.4546C15.6401 26.5637 16.8768 29.5446 19.0754 31.7431C21.2739 33.9416 24.2547 35.1783 27.3639 35.1819Z"
                        fill="black"
                      />
                    </svg>
                  </button>

                  {/* Buy More Button */}
                  <button
                    className="btn-next popup-btn"
                    type="button"
                    style={{ bottom: "-85px" }}
                    onClick={() => setPopupBuy(true)}
                  >
                    <span>{t("common.buymore")}</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {cameraAccessError && (
        <div className="scan-popup">
          <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />

          <div className="biscuit-error-box">
            <div className="error-bis-scroll">
              <div className="bis-error-scroll-inner">
                <img
                  src="assets/images/language-popup-bg.webp"
                  className="img-fit"
                  alt="Language Background"
                />

                <div className="bis-error-text">
                  <img
                    src={
                      language === "ta"
                        ? "assets/images/oops-tamil-icon.webp"
                        : "assets/images/oops-icon.webp"
                    }
                    className="oops-icon"
                    alt="Oops Icon"
                  />

                  <p
                    style={{
                      maxWidth: "220px",
                      margin: "0 auto",
                      textAlign: "center",
                      lineHeight: "1.2",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: "bold",
                        display: "block",
                        marginBottom: "10px",
                        fontSize: "14px",
                      }}
                    >
                      {t("popup.cameraAccessTitle")}
                    </span>

                    {t("popup.cameraAccessText")}
                  </p>
                </div>
              </div>

              <div className="bis-error-scroll-footer">
                <button
                  className="btn-next error-bis-btn"
                  onClick={() => window.location.reload() || navigate.reload()}
                  type="button"
                >
                  <span>{t("popup.cameraAccessButton")}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {popupBuy && (
        <BuyMore onClose={() => setPopupBuy(false)} />
      )}
    </>
  )
}

export default MachineCodeScan
